import { Card, cn } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, FileText, FolderKanban } from "lucide-react";
import { useMemo } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { useMonthlyReportsQuery } from "@/modules/management/monthly-report/presentation/api/queries";
import { useProjectStats } from "@/modules/management/project/presentation/api/queries";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { dashboardLayout } from "../lib/dashboard-layout";
import { DashboardSection } from "./DashboardSection";
import { DashboardStatCard } from "./DashboardStatCard";
import { DashboardStatMetricGrid } from "./DashboardStatMetricGrid";

const MONTH_NAMES_LO = [
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ເມ.ສ.",
  "ພ.ພ.",
  "ມິ.ຖ.",
  "ກ.ລ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
] as const;

function formatReportPeriod(year: number, month: number): string {
  const name = MONTH_NAMES_LO[month - 1] ?? String(month);
  return `${name} ${year}`;
}

export function DashboardManagementPanel() {
  const { hasAny } = usePermissions();
  const navigate = useNavigate();

  const canProject = hasAny([
    Permissions.mgmt_project.read,
    Permissions.mgmt_project.list,
  ]);
  const canMonthly = hasAny([
    Permissions.mgmt_monthly_report.read,
    Permissions.mgmt_monthly_report.list,
  ]);

  const now = new Date();
  const periodYear = now.getFullYear();
  const periodMonth = now.getMonth() + 1;

  const { data: projectStats, isLoading: projectLoading } = useProjectStats(
    {},
    { enabled: canProject },
  );
  const monthlyQuery = useMonthlyReportsQuery(
    {
      limit: 5,
      offset: 0,
      periodYear,
      periodMonth,
      sort: [{ field: "createdAt", dir: "desc" }],
    },
    { enabled: canMonthly },
  );

  const recentReports = monthlyQuery.data?.data ?? [];
  const reportCount = monthlyQuery.data?.meta.total ?? 0;

  const periodLabel = useMemo(
    () => formatReportPeriod(periodYear, periodMonth),
    [periodYear, periodMonth],
  );

  if (!canProject && !canMonthly) return null;

  const overall = projectStats?.overall;
  const tasks = projectStats?.tasks;

  return (
    <DashboardSection title="ການຈັດການ">
      <div className={dashboardLayout.managementCardGrid}>
        {canProject ? (
          <DashboardStatCard
            title="ໂຄງການ"
            subtitle="ພາບລວມທົ່ວອົງກອນ"
            icon={FolderKanban}
            accent="amber"
            to="/app/management/projects/summary"
            footer="ເບິ່ງສະຫຼຸບໂຄງການ"
            highlight={
              overall
                ? {
                    label: "ຄືບໜ້າ",
                    value: `${overall.avgProgress}%`,
                  }
                : undefined
            }
          >
            {projectLoading ? (
              <DashboardStatMetricGrid loading metrics={[]} />
            ) : overall ? (
              <DashboardStatMetricGrid
                metrics={[
                  {
                    label: "ກຳລັງດຳເນີນ",
                    value: overall.active,
                    tone: "success",
                  },
                  { label: "ທັງໝົດ", value: overall.total },
                  {
                    label: "ສຳເລັດ",
                    value: overall.completed,
                    tone: "muted",
                  },
                  {
                    label: "Task ຄ້າງ",
                    value: tasks?.pending ?? 0,
                    tone: (tasks?.pending ?? 0) > 0 ? "warning" : "muted",
                  },
                ]}
              />
            ) : (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            )}
          </DashboardStatCard>
        ) : null}

        {canMonthly ? (
          <DashboardStatCard
            title="ລາຍງານປະຈຳເດືອນ"
            subtitle={periodLabel}
            icon={FileText}
            accent="brand"
            to="/app/management/monthly-reports"
            footer="ເບິ່ງລາຍງານທັງໝົດ"
            highlight={{
              label: "ເດືອນນີ້",
              value: reportCount.toLocaleString("lo-LA"),
            }}
          >
            {monthlyQuery.isLoading ? (
              <DashboardStatMetricGrid loading metrics={[]} />
            ) : reportCount === 0 ? (
              <div
                className={`flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-border/80 bg-muted/20 text-center ${dashboardLayout.metricPad}`}
              >
                <p className="font-medium text-muted-foreground text-sm">
                  ຍັງບໍ່ມີລາຍງານເດືອນນີ້
                </p>
                <p className="mt-1 text-muted-foreground text-xs">
                  ກົດເພື່ອໄປສ້າງ ຫຼື ອັບໂຫຼດລາຍງານ
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm leading-relaxed">
                ມີ{" "}
                <strong className="text-foreground">
                  {reportCount.toLocaleString("lo-LA")}
                </strong>{" "}
                ລາຍງານຈາກພະແນກ/ທີມ — ເບິ່ງລາຍການລ່າສຸດຂ້າງລຸ່ມ
              </p>
            )}
          </DashboardStatCard>
        ) : null}
      </div>

      {canMonthly && recentReports.length > 0 ? (
        <Card
          className={cn(
            dashboardLayout.cardChrome,
            "mt-4 overflow-hidden border border-border/80 shadow-sm",
          )}
        >
          <div
            className={`flex items-center justify-between gap-2 border-border/60 border-b bg-muted/25 ${dashboardLayout.barPad}`}
          >
            <p className="font-semibold text-sm">ລາຍງານລ່າສຸດ · {periodLabel}</p>
            <button
              type="button"
              className="flex items-center gap-0.5 text-primary text-xs hover:underline"
              onClick={() => navigate({ to: "/app/management/monthly-reports" })}
            >
              ທັງໝົດ
              <ChevronRight className="size-3.5" />
            </button>
          </div>
          <ul className="divide-y divide-border/60">
            {recentReports.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`group flex w-full items-center gap-3 text-left transition-colors hover:bg-muted/40 ${dashboardLayout.barPad}`}
                  onClick={() =>
                    navigate({
                      to: "/app/management/monthly-reports/$id",
                      params: { id: row.id },
                    })
                  }
                >
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/10">
                    <FileText className="size-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-1 font-medium text-sm">
                      {row.title?.trim() ||
                        row.departmentName ||
                        "ລາຍງານປະຈຳເດືອນ"}
                    </span>
                    {row.departmentName ? (
                      <span className="mt-0.5 block text-muted-foreground text-xs">
                        {row.departmentName}
                      </span>
                    ) : null}
                  </div>
                  <ChevronRight
                    className={cn(
                      "size-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-primary",
                    )}
                  />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </DashboardSection>
  );
}
